import { useState } from "react";
import { AlignLeft } from "lucide-react";
import { MAX_PRODUCT_DESCRIPTION_LENGTH } from "@school-sales-ai/shared";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * A product's optional description — one or two sentences the agent uses to
 * introduce it (`builder.ts` appends it to the product's catalog line).
 *
 * Its own window rather than a column in the row: a paragraph per row would
 * push a 25-product catalog off the screen, and most rows have none. The row
 * shows an icon when one exists (`ProductDescriptionIndicator`), so what is
 * missing is still visible at a glance.
 *
 * «Guardar» saves right away, like «Medios»; there is no catalog-wide save.
 */
export function ProductDescriptionDialog({
  productName,
  noun,
  description,
  onSave,
  onClose,
}: {
  productName: string;
  /** "programa" or "producto" — whatever this catalog calls a row. */
  noun: string;
  description: string | null;
  onSave: (description: string) => Promise<boolean>;
  onClose: () => void;
}): React.JSX.Element {
  const [text, setText] = useState(description ?? "");
  const [saving, setSaving] = useState(false);
  const unchanged = text.trim() === (description ?? "").trim();

  async function save(): Promise<void> {
    setSaving(true);
    const ok = await onSave(text);
    setSaving(false);
    if (ok) onClose();
  }

  return (
    <Dialog open onOpenChange={(open: boolean) => !open && !saving && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Descripción — {productName}</DialogTitle>
          <DialogDescription>
            Una o dos frases que el agente usa para presentar este {noun}. Los detalles largos
            van en la Base de conocimiento.
          </DialogDescription>
        </DialogHeader>

        <div>
          <textarea
            rows={4}
            value={text}
            maxLength={MAX_PRODUCT_DESCRIPTION_LENGTH}
            disabled={saving}
            aria-label={`Descripción de ${productName}`}
            onChange={(e) => setText(e.target.value)}
            className="w-full resize-none rounded-lg border border-ink-200 bg-white px-3 py-2 text-[13px] text-ink-900 placeholder:text-ink-300 focus:border-moss focus:outline-none"
          />
          <p className="mt-1 text-right text-[11.5px] tabular-nums text-ink-400">
            {text.length}/{MAX_PRODUCT_DESCRIPTION_LENGTH}
          </p>
        </div>

        <DialogFooter>
          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            className="rounded-lg border border-ink-200 px-4 py-2 text-sm font-medium text-ink-700 hover:bg-ink-050 disabled:opacity-40"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={saving || unchanged}
            onClick={() => void save()}
            className="rounded-lg bg-lime px-4 py-2 text-sm font-semibold text-moss hover:bg-lime-dk disabled:opacity-40"
          >
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The row's sign that a description exists — nothing at all when it does not.
 * A fixed-width slot, like the media icons, so every row's inputs keep one width.
 */
export function ProductDescriptionIndicator({
  productName,
  description,
  onOpen,
}: {
  productName: string;
  description: string | null;
  onOpen: () => void;
}): React.JSX.Element {
  return (
    <div className="flex w-6 shrink-0 items-center justify-center self-center">
      {description && (
        <button
          type="button"
          onClick={onOpen}
          aria-label={`Descripción de ${productName}`}
          title={description}
          className="rounded p-0.5 text-ink-500 hover:bg-ink-050 hover:text-ink-700"
        >
          <AlignLeft size={14} />
        </button>
      )}
    </div>
  );
}
